import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../contextStore/AuthContext';
import { ToastContext } from '../../contextStore/ToastContext';
import { createBuyRequest } from '../../firebase/offers';
import { CATEGORIES } from '../../data/categories';
import ButtonSpinner from '../UI/ButtonSpinner';
import './CreateBuyOfferModal.css';

function CreateBuyOfferModal({ onClose, onSuccess }) {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { addToast } = useContext(ToastContext);

  const [productName, setProductName] = useState('');
  const [category, setCategory] = useState('');
  const [desiredPrice, setDesiredPrice] = useState('');
  const [description, setDescription] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [deliveryPreference, setDeliveryPreference] = useState('pickup');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [visible, setVisible] = useState(false);
  const [errors, setErrors] = useState({});

  const PAYMENT_OPTIONS = [
    { value: 'cash', label: t('buyOffer.paymentCash'), icon: '€' },
    { value: 'upi', label: t('buyOffer.paymentUpi'), icon: '⚡' },
    { value: 'bank_transfer', label: t('buyOffer.paymentBankTransfer'), icon: '🏦' },
  ];

  const DELIVERY_OPTIONS = [
    { value: 'pickup', label: t('buyOffer.deliveryPickup'), icon: '📍' },
    { value: 'delivery', label: t('buyOffer.deliveryDelivery'), icon: '🚚' },
    { value: 'meet', label: t('buyOffer.deliveryMeet'), icon: '🤝' },
  ];

  // Entrance animation
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  // Escape key handler
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const validateForm = () => {
    const newErrors = {};
    
    if (!productName.trim()) {
      newErrors.productName = t('buyOffer.errorProductNameRequired');
    }
    
    if (!category) {
      newErrors.category = t('buyOffer.errorCategoryRequired');
    }
    
    if (!desiredPrice || isNaN(Number(desiredPrice)) || Number(desiredPrice) <= 0) {
      newErrors.desiredPrice = t('buyOffer.errorDesiredPriceRequired');
    }
    
    if (!description.trim()) {
      newErrors.description = t('buyOffer.errorDescriptionRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      addToast?.(t('auth.loginTitle'), 'error');
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      await createBuyRequest({
        productName: productName.trim(),
        category,
        desiredPrice: Number(desiredPrice),
        description: description.trim(),
        paymentMethod,
        deliveryPreference,
        userId: user.uid,
      });
      
      setSuccess(true);
      addToast?.(t('buyOffer.offerCreatedSuccessfully'), 'success');
      
      setTimeout(() => {
        onSuccess?.();
        handleClose();
        resetForm();
      }, 2000);
    } catch (err) {
      console.error('CreateBuyOfferModal: failed to create buy request', err);
      addToast?.(t('common.error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(() => onClose(), 280);
  }, [onClose]);

  const resetForm = () => {
    setProductName('');
    setCategory('');
    setDesiredPrice('');
    setDescription('');
    setPaymentMethod('cash');
    setDeliveryPreference('pickup');
    setErrors({});
    setSuccess(false);
  };

  return (
    <div className={`createBuyOfferModal ${visible ? 'createBuyOfferModalVisible' : ''}`}>
      <div className="createBuyOfferModalContent">
        <button className="createBuyOfferModalClose" onClick={handleClose}>
          ×
        </button>
        
        {!success ? (
          <>
            <div className="createBuyOfferModalHeader">
              <h2>{t('buyOffer.title')}</h2>
              <p className="createBuyOfferModalSubtitle">
                {t('buyOffer.subtitle')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="createBuyOfferForm">
              <div className="createBuyOfferFormField">
                <label htmlFor="productName">{t('buyOffer.productName')}</label>
                <input
                  id="productName"
                  type="text"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder={t('buyOffer.productNamePlaceholder')}
                  className={`createBuyOfferInput ${errors.productName ? 'createBuyOfferInputError' : ''}`}
                />
                {errors.productName && (
                  <span className="createBuyOfferError">{errors.productName}</span>
                )}
              </div>

              <div className="createBuyOfferFormField">
                <label htmlFor="category">{t('buyOffer.category')}</label>
                <select
                  id="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className={`createBuyOfferSelect ${errors.category ? 'createBuyOfferInputError' : ''}`}
                >
                  <option value="">{t('buyOffer.selectCategory')}</option>
                  {CATEGORIES.map((cat) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <span className="createBuyOfferError">{errors.category}</span>
                )}
              </div>

              <div className="createBuyOfferFormField">
                <label htmlFor="desiredPrice">{t('buyOffer.desiredPrice')}</label>
                <input
                  id="desiredPrice"
                  type="number"
                  value={desiredPrice}
                  onChange={(e) => setDesiredPrice(e.target.value)}
                  placeholder={t('buyOffer.desiredPricePlaceholder')}
                  min="0"
                  step="0.01"
                  className={`createBuyOfferInput ${errors.desiredPrice ? 'createBuyOfferInputError' : ''}`}
                />
                {errors.desiredPrice && (
                  <span className="createBuyOfferError">{errors.desiredPrice}</span>
                )}
              </div>

              <div className="createBuyOfferFormField">
                <label htmlFor="description">{t('buyOffer.description')}</label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('buyOffer.descriptionPlaceholder')}
                  rows={4}
                  className={`createBuyOfferTextarea ${errors.description ? 'createBuyOfferInputError' : ''}`}
                />
                {errors.description && (
                  <span className="createBuyOfferError">{errors.description}</span>
                )}
              </div>

              <div className="createBuyOfferFormField">
                <label>{t('buyOffer.paymentMethod')}</label>
                <div className="createBuyOfferOptions">
                  {PAYMENT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`createBuyOfferOption ${
                        paymentMethod === option.value ? 'createBuyOfferOptionActive' : ''
                      }`}
                      onClick={() => setPaymentMethod(option.value)}
                    >
                      <span className="createBuyOfferOptionIcon">{option.icon}</span>
                      <span className="createBuyOfferOptionLabel">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="createBuyOfferFormField">
                <label>{t('buyOffer.deliveryPreference')}</label>
                <div className="createBuyOfferOptions">
                  {DELIVERY_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`createBuyOfferOption ${
                        deliveryPreference === option.value ? 'createBuyOfferOptionActive' : ''
                      }`}
                      onClick={() => setDeliveryPreference(option.value)}
                    >
                      <span className="createBuyOfferOptionIcon">{option.icon}</span>
                      <span className="createBuyOfferOptionLabel">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                className="createBuyOfferSubmit"
                disabled={loading}
              >
                {loading ? <ButtonSpinner /> : t('buyOffer.submit')}
              </button>
            </form>
          </>
        ) : (
          <div className="createBuyOfferSuccess">
            <div className="createBuyOfferSuccessIcon">✓</div>
            <h3>{t('buyOffer.offerCreatedTitle')}</h3>
            <p>{t('buyOffer.offerCreatedMessage')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateBuyOfferModal;